import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Test = {
  id: number;
  title: string;
  topic_name?: string;
  course_name: string;
  assigned_users: string;
  question_count: number;
  status: string;
  test_type?: string;
  assigned_departments?: string;
  pass_mark?: number;
  total_marks?: number;
  start_date?: string;
  end_date?: string;
  pdf_url?: string;
  questions_json?: string;
};

type Course = {
  id: number;
  category: string;
  name: string;
};

type User = {
  id: number;
  username?: string;
  name?: string;
  email: string;
  role: string;
  department: string;
};

type QuestionItem = {
  questionText: string;
  marks: number;
  optionCount: number;
  options: string[];
  correctOptionIndexes: number[];
};

function createEmptyQuestion(optionCount: number, marks: number): QuestionItem {
  return {
    questionText: "",
    marks,
    optionCount,
    options: Array.from({ length: optionCount }, () => ""),
    correctOptionIndexes: []
  };
}

function TestsPage() {
  const navigate = useNavigate();
const location = useLocation();

  const [tests, setTests] = useState<Test[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTestId, setEditingTestId] = useState<number | null>(null);

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const [testType, setTestType] = useState("questions");
  const [defaultOptionCount, setDefaultOptionCount] = useState(4);
  const [defaultMarks, setDefaultMarks] = useState(1);
  const [testTitle, setTestTitle] = useState("");
  const [topicName, setTopicName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [passMark, setPassMark] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [questions, setQuestions] = useState<QuestionItem[]>([
    createEmptyQuestion(4, 1)
  ]);

  const [expandedQuestionIndexes, setExpandedQuestionIndexes] = useState<number[]>([0]);

  useEffect(() => {
    fetchTests();
    fetchCourses();
    fetchUsers();
  }, []);

  useEffect(() => {
  const searchParams = new URLSearchParams(location.search);
  const prefilledCourseName = searchParams.get("courseName");
  const prefilledCategory = searchParams.get("category");

  if (prefilledCourseName || prefilledCategory) {
    setShowCreateForm(true);
    setCourseName(prefilledCourseName || "");
    setTopicName(prefilledCategory || "");
  }
}, [location.search]);

  const fetchTests = async () => {
    try {
      const response = await fetch("/tests/");
      const data = await response.json();
      setTests(data);
    } catch (error) {
      console.error("テスト一覧の取得に失敗しました:", error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch("/courses/");
      const data = await response.json();
      setCourses(data);
    } catch (error) {
      console.error("コース一覧の取得に失敗しました:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch("/users/");
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("ユーザー一覧の取得に失敗しました:", error);
    }
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setEditingTestId(null);
    setTestTitle("");
    setTopicName("");
    setCourseName("");
    setPassMark(0);
    setStartDate("");
    setEndDate("");
    setSelectedDepartments([]);
    setSelectedUserIds([]);
    setQuestions([createEmptyQuestion(4, 1)]);
    setDefaultOptionCount(4);
    setDefaultMarks(1);
    setExpandedQuestionIndexes([0]);
    setTestType("questions");
  };

  const topicOptions = Array.from(
    new Set(courses.map((course) => course.category).filter(Boolean))
  );

  const courseOptions = courses.map((course) => course.name);

  const departmentOptions = Array.from(
    new Set(users.map((user) => user.department).filter(Boolean))
  );

  const filteredUsersByDepartment = users.filter(
  (user) =>
    selectedDepartments.includes(user.department) &&
    user.role === "employee"
);


const areAllDepartmentsSelected =
  departmentOptions.length > 0 &&
  departmentOptions.every((department) =>
    selectedDepartments.includes(department)
  );

const filteredUserIds = filteredUsersByDepartment.map((user) => user.id);

const areAllFilteredUsersSelected =
  filteredUserIds.length > 0 &&
  filteredUserIds.every((userId) => selectedUserIds.includes(userId));



  const totalMarks = questions.reduce(
    (sum, question) => sum + Number(question.marks || 0),
    0
  );

  const handleDepartmentToggle = (department: string) => {
    setSelectedDepartments((prev) => {
      const isSelected = prev.includes(department);

      if (isSelected) {
        setSelectedUserIds((currentUserIds) =>
          currentUserIds.filter((userId) => {
            const user = users.find((item) => item.id === userId);
            return user?.department !== department;
          })
        );

        return prev.filter((item) => item !== department);
      }

      return [...prev, department];
    });
  };

  const handleUserToggle = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };


  const handleSelectAllDepartments = () => {
  if (areAllDepartmentsSelected) {
    setSelectedDepartments([]);
    setSelectedUserIds([]);
    return;
  }

  setSelectedDepartments(departmentOptions);
};

const handleSelectAllUsers = () => {
  if (areAllFilteredUsersSelected) {
    setSelectedUserIds((prev) =>
      prev.filter((userId) => !filteredUserIds.includes(userId))
    );
    return;
  }

  setSelectedUserIds((prev) =>
    Array.from(new Set([...prev, ...filteredUserIds]))
  );
};

  const handleQuestionTextChange = (index: number, value: string) => {
    setQuestions((prev) =>
      prev.map((question, questionIndex) =>
        questionIndex === index
          ? { ...question, questionText: value }
          : question
      )
    );
  };

  const handleQuestionMarksChange = (index: number, value: string) => {
    setQuestions((prev) =>
      prev.map((question, questionIndex) =>
        questionIndex === index
          ? { ...question, marks: Number(value) }
          : question
      )
    );
  };

  const handleQuestionOptionCountChange = (index: number, value: string) => {
    const newOptionCount = Number(value);

    setQuestions((prev) =>
      prev.map((question, questionIndex) => {
        if (questionIndex !== index) return question;

        const updatedOptions = Array.from(
          { length: newOptionCount },
          (_, optionIndex) => question.options[optionIndex] || ""
        );

        const updatedCorrectOptionIndexes = question.correctOptionIndexes.filter(
          (optionIndex) => optionIndex < newOptionCount
        );

        return {
          ...question,
          optionCount: newOptionCount,
          options: updatedOptions,
          correctOptionIndexes: updatedCorrectOptionIndexes
        };
      })
    );
  };

  const handleOptionTextChange = (
    questionIndex: number,
    optionIndex: number,
    value: string
  ) => {
    setQuestions((prev) =>
      prev.map((question, currentQuestionIndex) => {
        if (currentQuestionIndex !== questionIndex) return question;

        const updatedOptions = [...question.options];
        updatedOptions[optionIndex] = value;

        return {
          ...question,
          options: updatedOptions
        };
      })
    );
  };

  const handleCorrectOptionToggle = (
    questionIndex: number,
    optionIndex: number
  ) => {
    setQuestions((prev) =>
      prev.map((question, currentQuestionIndex) => {
        if (currentQuestionIndex !== questionIndex) return question;

        const alreadyCorrect = question.correctOptionIndexes.includes(optionIndex);

        return {
          ...question,
          correctOptionIndexes: alreadyCorrect
            ? question.correctOptionIndexes.filter((index) => index !== optionIndex)
            : [...question.correctOptionIndexes, optionIndex]
        };
      })
    );
  };

  const applyDefaultOptionCountToAllQuestions = () => {
    setQuestions((prev) =>
      prev.map((question) => {
        const updatedOptions = Array.from(
          { length: defaultOptionCount },
          (_, optionIndex) => question.options[optionIndex] || ""
        );

        const updatedCorrectOptionIndexes = question.correctOptionIndexes.filter(
          (optionIndex) => optionIndex < defaultOptionCount
        );

        return {
          ...question,
          optionCount: defaultOptionCount,
          options: updatedOptions,
          correctOptionIndexes: updatedCorrectOptionIndexes
        };
      })
    );
  };

  const applyDefaultMarksToAllQuestions = () => {
    setQuestions((prev) =>
      prev.map((question) => ({
        ...question,
        marks: defaultMarks
      }))
    );
  };

  const addQuestion = () => {
    setQuestions((prev) => {
      const newIndex = prev.length;
      setExpandedQuestionIndexes([newIndex]);
      return [...prev, createEmptyQuestion(defaultOptionCount, defaultMarks)];
    });
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) =>
      prev.filter((_, questionIndex) => questionIndex !== index)
    );
  };

  const toggleQuestionExpand = (index: number) => {
    setExpandedQuestionIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };

  const getScorePerCorrectOption = (question: QuestionItem) => {
    if (question.correctOptionIndexes.length === 0) {
      return 0;
    }

    return Number(question.marks || 0) / question.correctOptionIndexes.length;
  };

  const getAssignedUserCount = (assignedUsers: string) => {
    try {
      const parsedUsers = JSON.parse(assignedUsers);
      return Array.isArray(parsedUsers) ? parsedUsers.length : 0;
    } catch {
      return 0;
    }
  };

  const handleDeleteTest = async (testId: number) => {
    const confirmDelete = window.confirm("このテストを削除しますか？");

    if (!confirmDelete) return;

    try {
      const response = await fetch(`/tests/${testId}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        throw new Error("削除に失敗しました。");
      }

      await fetchTests();
      alert("テストを削除しました。");
    } catch (error) {
      console.error(error);
      alert("削除中にエラーが発生しました。");
    }
  };

  const handleEditTest = (test: Test) => {
    setEditingTestId(test.id);
    setShowCreateForm(true);

    setTestTitle(test.title || "");
    setTopicName(test.topic_name || "");
    setCourseName(test.course_name || "");
    setTestType(test.test_type || "questions");
    setPassMark(test.pass_mark || 0);
    setStartDate(test.start_date || "");
    setEndDate(test.end_date || "");

    try {
      setSelectedDepartments(
        test.assigned_departments ? JSON.parse(test.assigned_departments) : []
      );
    } catch {
      setSelectedDepartments([]);
    }

    try {
      const parsedUsers = test.assigned_users ? JSON.parse(test.assigned_users) : [];
      setSelectedUserIds(parsedUsers.map((user: { id: number }) => user.id));
    } catch {
      setSelectedUserIds([]);
    }

    try {
      const parsedQuestions = test.questions_json
        ? JSON.parse(test.questions_json)
        : [createEmptyQuestion(4, 1)];

      setQuestions(parsedQuestions);
    } catch {
      setQuestions([createEmptyQuestion(4, 1)]);
    }

    setExpandedQuestionIndexes([0]);
  };

  const handleSaveTest = async (saveType: "draft" | "published") => {
    try {
      if (!testTitle || !courseName) {
        alert("テスト名とコース名を入力してください。");
        return;
      }

      if (selectedUserIds.length === 0) {
        alert("対象ユーザーを選択してください。");
        return;
      }

      const selectedUsers = users.filter((user) =>
        selectedUserIds.includes(user.id)
      );

      const payload = {
        title: testTitle,
        topic_name: topicName,
        course_name: courseName,
        test_type: testType,
        assigned_departments: JSON.stringify(selectedDepartments),
        assigned_users: JSON.stringify(
          selectedUsers.map((user) => ({
            id: user.id,
            name: user.username || user.name || user.email,
            email: user.email,
            role: user.role,
            department: user.department
          }))
        ),
        question_count: testType === "questions" ? questions.length : 0,
        pass_mark: passMark,
        total_marks: testType === "questions" ? totalMarks : 0,
        start_date: startDate,
        end_date: endDate,
        pdf_url: "",
        questions_json: testType === "questions" ? JSON.stringify(questions) : "",
        status: saveType
      };

      const response = await fetch(
        editingTestId
          ? `/tests/${editingTestId}`
          : "/tests/",
        {
          method: editingTestId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error("テストの保存に失敗しました。");
      }

      await fetchTests();
      resetForm();

      alert(
        saveType === "draft"
          ? "テストを一時保存しました。"
          : "テストを公開保存しました。"
      );
    } catch (error) {
      console.error(error);
      alert("テストの保存中にエラーが発生しました。");
    }
  };

  const getTodayInJapan = () => {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).format(new Date());
  };

  const getTestStatusLabel = (test: Test) => {
    const today = getTodayInJapan();

    if (test.status === "draft") {
      return "下書き";
    }

    if (test.end_date && test.end_date < today) {
      return "終了";
    }

    if (test.start_date && test.start_date > today) {
      return "公開予定";
    }

    return "公開中";
  };

  const filteredTests = tests.filter((test) =>
    test.title.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        padding: "36px",
        fontFamily: "Arial, sans-serif"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "30px", color: "#0f172a" }}>
            テスト管理
          </h1>
          <p style={{ marginTop: "10px", color: "#64748b", fontSize: "15px" }}>
            テストの作成・閲覧・編集・削除を行います
          </p>
        </div>

        <div style={{ display: "flex", gap: "14px" }}>
          <button onClick={() => navigate("/admin-dashboard")} style={backButtonStyle}>
            ← ダッシュボードへ戻る
          </button>

          <button onClick={() => setShowCreateForm(true)} style={primaryButtonStyle}>
            ＋ {editingTestId ? "テスト編集" : "新規テスト作成"}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, marginBottom: "20px", color: "#0f172a" }}>
            {editingTestId ? "テスト編集" : "新規テスト作成"}
          </h2>

          <div style={formGridStyle}>
            <div>
              <label style={labelStyle}>テスト名</label>
              <input
                type="text"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                placeholder="テスト名を入力"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>トピック名</label>
              <select
                value={topicName}
                onChange={(e) => setTopicName(e.target.value)}
                style={inputStyle}
              >
                <option value="">選択してください</option>
                {topicOptions.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>コース名</label>
              <select
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                style={inputStyle}
              >
                <option value="">選択してください</option>
                {courseOptions.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>テスト形式</label>
              <select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                style={inputStyle}
              >
                <option value="questions">問題入力</option>
              </select>
            </div>

            <FormInput label="問題数" placeholder="自動計算されます" type="number" />

            <div>
              <label style={labelStyle}>合格点</label>
              <input
                type="number"
                value={passMark}
                onChange={(e) => setPassMark(Number(e.target.value))}
                placeholder="例：70"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>開始日</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>終了日</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          

          {testType === "questions" && (
            <div style={{ marginTop: "22px" }}>
              <label style={labelStyle}>問題入力</label>

              <div style={defaultOptionBoxStyle}>
                <div>
                  <label style={labelStyle}>全問題の標準選択肢数</label>
                  <select
                    value={defaultOptionCount}
                    onChange={(e) => setDefaultOptionCount(Number(e.target.value))}
                    style={{ ...inputStyle, maxWidth: "220px" }}
                  >
                    <option value={1}>1択</option>
                    <option value={2}>2択</option>
                    <option value={3}>3択</option>
                    <option value={4}>4択</option>
                    <option value={5}>5択</option>
                    <option value={6}>6択</option>
                  </select>
                </div>

                <button
                  onClick={applyDefaultOptionCountToAllQuestions}
                  style={secondaryButtonStyle}
                >
                  選択肢数を全問題に適用
                </button>

                <div>
                  <label style={labelStyle}>全問題の標準配点</label>
                  <input
                    type="number"
                    value={defaultMarks}
                    onChange={(e) => setDefaultMarks(Number(e.target.value))}
                    style={{ ...inputStyle, maxWidth: "220px" }}
                  />
                </div>

                <button
                  onClick={applyDefaultMarksToAllQuestions}
                  style={secondaryButtonStyle}
                >
                  配点を全問題に適用
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "16px" }}>
                {questions.map((question, index) => {
                  const isExpanded = expandedQuestionIndexes.includes(index);

                  return (
                    <div key={index} style={questionBoxStyle}>
                      <div
                        onClick={() => toggleQuestionExpand(index)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer"
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {isExpanded ? "▼" : "▶"} 問題 {index + 1}
                          {question.questionText
                            ? `：${question.questionText.slice(0, 40)}`
                            : "：未入力"}
                        </div>

                        <div style={{ fontSize: "13px", fontWeight: 700, color: "#475569" }}>
                          {Number(question.marks || 0)}点 / {question.optionCount}択 / 正解{" "}
                          {question.correctOptionIndexes.length}個
                        </div>
                      </div>

                      {isExpanded && (
                        <>
                          <textarea
                            value={question.questionText}
                            onChange={(e) =>
                              handleQuestionTextChange(index, e.target.value)
                            }
                            placeholder="問題文を入力"
                            style={{ ...textareaStyle, marginTop: "14px" }}
                          />

                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              marginTop: "10px",
                              alignItems: "center",
                              flexWrap: "wrap"
                            }}
                          >
                            <div>
                              <label style={smallLabelStyle}>配点</label>
                              <input
                                type="number"
                                value={question.marks}
                                onChange={(e) =>
                                  handleQuestionMarksChange(index, e.target.value)
                                }
                                placeholder="配点"
                                style={{ ...inputStyle, maxWidth: "160px" }}
                              />
                            </div>

                            <div>
                              <label style={smallLabelStyle}>この問題の選択肢数</label>
                              <select
                                value={question.optionCount}
                                onChange={(e) =>
                                  handleQuestionOptionCountChange(index, e.target.value)
                                }
                                style={{ ...inputStyle, maxWidth: "180px" }}
                              >
                                <option value={1}>1択</option>
                                <option value={2}>2択</option>
                                <option value={3}>3択</option>
                                <option value={4}>4択</option>
                                <option value={5}>5択</option>
                                <option value={6}>6択</option>
                              </select>
                            </div>

                            {questions.length > 1 && (
                              <button
                                onClick={() => removeQuestion(index)}
                                style={deleteButtonStyle}
                              >
                                問題を削除
                              </button>
                            )}
                          </div>

                          <div style={scoreInfoStyle}>
                            正解選択肢数：{question.correctOptionIndexes.length}個 /
                            1つあたりの得点：
                            {getScorePerCorrectOption(question).toFixed(2)}点
                          </div>

                          <div style={optionsBoxStyle}>
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: "10px",
                                color: "#0f172a"
                              }}
                            >
                              選択肢と正解設定（複数選択可）
                            </div>

                            {question.options.map((option, optionIndex) => {
                              const isCorrect =
                                question.correctOptionIndexes.includes(optionIndex);

                              return (
                                <div key={optionIndex} style={optionRowStyle}>
                                  <input
                                    type="checkbox"
                                    checked={isCorrect}
                                    onChange={() =>
                                      handleCorrectOptionToggle(index, optionIndex)
                                    }
                                  />

                                  <span style={{ fontWeight: 700, minWidth: "70px" }}>
                                    選択肢 {optionIndex + 1}
                                  </span>

                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) =>
                                      handleOptionTextChange(
                                        index,
                                        optionIndex,
                                        e.target.value
                                      )
                                    }
                                    placeholder={`選択肢 ${optionIndex + 1} を入力`}
                                    style={inputStyle}
                                  />

                                  {isCorrect && (
                                    <span style={correctBadgeStyle}>正解</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px" }}>
                <button onClick={addQuestion} style={secondaryButtonStyle}>
                  ＋ 問題を追加
                </button>

                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  合計点：{totalMarks}点
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: "22px" }}>
  <label style={labelStyle}>部署選択（複数選択可）</label>

  <div style={checkboxBoxStyle}>
    {departmentOptions.length === 0 ? (
      <p style={emptyTextStyle}>部署が登録されていません</p>
    ) : (
      <>
        <label
          style={{
            ...checkboxLabelStyle,
            gridColumn: "1 / -1",
            backgroundColor: "#eff6ff",
            border: "1px solid #bfdbfe"
          }}
        >
          <input
            type="checkbox"
            checked={areAllDepartmentsSelected}
            onChange={handleSelectAllDepartments}
          />
          <span>
            {areAllDepartmentsSelected ? "すべて選択解除" : "すべて選択"}
          </span>
        </label>

        {departmentOptions.map((department) => (
          <label key={department} style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={selectedDepartments.includes(department)}
              onChange={() => handleDepartmentToggle(department)}
            />
            <span>{department}</span>
          </label>
        ))}
      </>
    )}
  </div>
</div>

          <div style={{ marginTop: "22px" }}>
            <label style={labelStyle}>対象ユーザー選択（複数選択可）</label>

            <div style={checkboxBoxStyle}>
  {selectedDepartments.length === 0 ? (
    <p style={emptyTextStyle}>先に部署を選択してください</p>
  ) : filteredUsersByDepartment.length === 0 ? (
    <p style={emptyTextStyle}>
      選択した部署にユーザーが登録されていません
    </p>
  ) : (
    <>
      <label
        style={{
          ...checkboxLabelStyle,
          gridColumn: "1 / -1",
          backgroundColor: "#eff6ff",
          border: "1px solid #bfdbfe"
        }}
      >
        <input
          type="checkbox"
          checked={areAllFilteredUsersSelected}
          onChange={handleSelectAllUsers}
        />
        <span>
          {areAllFilteredUsersSelected ? "すべて選択解除" : "すべて選択"}
        </span>
      </label>

      {filteredUsersByDepartment.map((user) => (
        <label key={user.id} style={checkboxLabelStyle}>
          <input
            type="checkbox"
            checked={selectedUserIds.includes(user.id)}
            onChange={() => handleUserToggle(user.id)}
          />
          <span>
            {user.username || user.name || user.email}（
            {user.department}・{user.role}）
          </span>
        </label>
      ))}
    </>
  )}
</div>

            <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: 700, color: "#334155" }}>
              選択中ユーザー：{selectedUserIds.length}名
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <button onClick={resetForm} style={backButtonStyle}>
              キャンセル
            </button>

            <button
              onClick={() => handleSaveTest("draft")}
              style={secondaryButtonStyle}
            >
              一時保存
            </button>

            <button
              onClick={() => handleSaveTest("published")}
              style={primaryButtonStyle}
            >
              公開保存
            </button>
          </div>
        </div>
      )}

      <div style={cardStyle}>
        <div style={filterBoxStyle}>
          <label style={labelStyle}>検索（テスト名）</label>

          <div style={{ display: "flex", gap: "12px" }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="テスト名で検索"
              style={inputStyle}
            />

            <button onClick={() => setSearchText("")} style={backButtonStyle}>
              クリア
            </button>
          </div>

          <div style={{ marginTop: "12px", color: "#334155", fontSize: "14px", fontWeight: 700 }}>
            表示件数：{filteredTests.length} 件
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb", textAlign: "left" }}>
              <th style={thStyle}>テスト名</th>
              <th style={thStyle}>カテゴリー</th>
              <th style={thStyle}>コース名</th>
              <th style={thStyle}>対象人数</th>
              <th style={thStyle}>問題数</th>
              <th style={thStyle}>ステータス</th>
              <th style={thStyle}>操作</th>
            </tr>
          </thead>

          <tbody>
            {filteredTests.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ ...tdStyle, textAlign: "center" }}>
                  テストが登録されていません
                </td>
              </tr>
            ) : (
              filteredTests.map((test) => (
                <tr key={test.id} style={{ borderBottom: "1px solid #eef2f7" }}>
                  <td style={tdStyle}>{test.title}</td>
                  <td style={tdStyle}>{test.topic_name || "-"}</td>
                  <td style={tdStyle}>{test.course_name}</td>
                  <td style={tdStyle}>{getAssignedUserCount(test.assigned_users)}名</td>
                  <td style={tdStyle}>{test.question_count}問</td>
                  <td style={tdStyle}>{getTestStatusLabel(test)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => handleEditTest(test)} style={editButtonStyle}>
                      編集
                    </button>
                    <button onClick={() => handleDeleteTest(test.id)} style={deleteButtonStyle}>
                      削除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormInput({
  label,
  placeholder,
  type = "text"
}: {
  label: string;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} placeholder={placeholder} style={inputStyle} disabled />
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: "24px",
  padding: "26px",
  marginBottom: "24px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  border: "1px solid #eef2f7"
};

const formGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "18px"
};

const filterBoxStyle: React.CSSProperties = {
  border: "1px solid #dbe3ef",
  borderRadius: "18px",
  padding: "18px",
  marginBottom: "26px"
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "14px",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: "8px"
};

const smallLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  color: "#475569",
  marginBottom: "6px"
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "44px",
  border: "1px solid #cbd5e1",
  borderRadius: "12px",
  padding: "0 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};



const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "90px",
  border: "1px solid #cbd5e1",
  borderRadius: "12px",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical"
};

const questionBoxStyle: React.CSSProperties = {
  border: "1px solid #dbe3ef",
  borderRadius: "16px",
  padding: "16px",
  backgroundColor: "#f8fafc"
};

const defaultOptionBoxStyle: React.CSSProperties = {
  border: "1px solid #dbe3ef",
  borderRadius: "16px",
  padding: "16px",
  backgroundColor: "#f8fafc",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: "16px",
  flexWrap: "wrap"
};

const optionsBoxStyle: React.CSSProperties = {
  marginTop: "16px",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "14px",
  backgroundColor: "white"
};

const optionRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "10px"
};

const scoreInfoStyle: React.CSSProperties = {
  marginTop: "12px",
  backgroundColor: "#eff6ff",
  color: "#1d4ed8",
  borderRadius: "12px",
  padding: "10px 12px",
  fontSize: "13px",
  fontWeight: 700
};

const correctBadgeStyle: React.CSSProperties = {
  backgroundColor: "#dcfce7",
  color: "#15803d",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 700,
  whiteSpace: "nowrap"
};



const checkboxBoxStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "14px",
  backgroundColor: "#f8fafc",
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "12px"
};

const checkboxLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  backgroundColor: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px",
  fontWeight: 700,
  color: "#0f172a",
  cursor: "pointer"
};

const emptyTextStyle: React.CSSProperties = {
  margin: 0,
  color: "#64748b",
  fontSize: "14px",
  fontWeight: 700
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: "#0f766e",
  color: "white",
  border: "none",
  borderRadius: "14px",
  padding: "13px 22px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

const secondaryButtonStyle: React.CSSProperties = {
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  border: "1px solid #bfdbfe",
  borderRadius: "12px",
  padding: "11px 16px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

const backButtonStyle: React.CSSProperties = {
  backgroundColor: "white",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "13px 22px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer"
};

const thStyle: React.CSSProperties = {
  padding: "14px 16px",
  fontSize: "14px",
  color: "#0f172a",
  fontWeight: 700
};

const tdStyle: React.CSSProperties = {
  padding: "18px 16px",
  fontSize: "15px",
  color: "#0f172a"
};

const editButtonStyle: React.CSSProperties = {
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  border: "none",
  borderRadius: "10px",
  padding: "9px 14px",
  marginRight: "8px",
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer"
};

const deleteButtonStyle: React.CSSProperties = {
  backgroundColor: "#fef2f2",
  color: "#dc2626",
  border: "none",
  borderRadius: "10px",
  padding: "9px 14px",
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer"
};

export default TestsPage;